import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Download, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LABEL = { pending_verification: "Pendiente de verificación", verified: "Verificado", rejected: "Rechazado" };

function ReviewDialog({ review, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const reject = review.action === "reject";
  async function submit() {
    setBusy(true);
    try {
      await api.post(`/service-orders/${review.soId}/payment-proofs/${review.id}/${review.action}`, { reason: reason || null, internal_comment: comment || null });
      toast.success(reject ? "Comprobante rechazado" : "Comprobante verificado");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="proof-review-dialog">
        <DialogHeader><DialogTitle>{reject ? "Rechazar comprobante" : "Verificar comprobante"}</DialogTitle></DialogHeader>
        {reject && <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (lo verá el cliente)" data-testid="proof-reject-reason" />}
        <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comentario interno (opcional, no lo ve el cliente)" data-testid="proof-internal-comment" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || (reject && !reason.trim())} className={reject ? "" : "btn-tenant"} variant={reject ? "destructive" : "default"} data-testid="proof-review-confirm">
            {busy ? "Guardando…" : reject ? "Rechazar" : "Marcar como verificado"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentProofsCard({ serviceOrder }) {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);
  const load = useCallback(() => api.get(`/service-orders/${serviceOrder.id}/payment-proofs`).then((r) => setRows(r.data)).catch((e) => { if (e?.response?.status !== 403) toast.error(formatApiError(e)); setRows([]); }), [serviceOrder.id]);
  useEffect(() => { load(); }, [load]);

  async function download(p) {
    try {
      const r = await api.get(`/service-orders/${serviceOrder.id}/payment-proofs/${p.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = p.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!rows.length) return null;
  return (
    <Card className="border-border p-5 space-y-3" data-testid="payment-proofs-card">
      <div className="flex items-center gap-2 text-sm font-medium"><Receipt className="h-4 w-4" /> Comprobantes del cliente (portal)</div>
      {rows.map((p) => (
        <div key={p.id} className="border border-border rounded-md p-3 text-sm space-y-1" data-testid={`proof-row-${p.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium break-all">{p.filename}</span>
            <Badge variant={p.status === "rejected" ? "destructive" : "secondary"} data-testid={`proof-status-${p.id}`}>{LABEL[p.status]}</Badge>
            <span className="text-xs text-muted-foreground">Cotización {p.quote_number} · {new Date(p.uploaded_at).toLocaleString("es-MX")} · {p.uploaded_by_name}{p.versions > 1 ? ` · versión ${p.versions}` : ""}</span>
          </div>
          {p.rejection_reason && <div className="text-xs">Motivo: {p.rejection_reason}</div>}
          {p.internal_comment && <div className="text-xs text-muted-foreground">Interno: {p.internal_comment}</div>}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => download(p)} data-testid={`proof-download-${p.id}`}><Download className="h-3.5 w-3.5 mr-1" /> Descargar</Button>
            {p.status === "pending_verification" && hasPermission("payments:edit") && (
              <>
                <Button size="sm" variant="outline" onClick={() => setReview({ id: p.id, soId: serviceOrder.id, action: "verify" })} data-testid={`proof-verify-${p.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Verificar</Button>
                <Button size="sm" variant="outline" onClick={() => setReview({ id: p.id, soId: serviceOrder.id, action: "reject" })} data-testid={`proof-reject-${p.id}`}><XCircle className="h-3.5 w-3.5 mr-1 text-red-600" /> Rechazar</Button>
              </>
            )}
          </div>
        </div>
      ))}
      {review && <ReviewDialog review={review} onClose={() => setReview(null)} onDone={() => { setReview(null); load(); }} />}
    </Card>
  );
}
